import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Plus, Pencil, Trash2, Shield, Clock, Play, CalendarPlus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { getTeams, getMatches, createMatch, updateMatch, deleteMatch } from "../lib/api";
import { toast } from "sonner";

const DEFAULT_TIMES = ["18:00", "19:00", "20:00"];

export default function Matches() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [matchDayDialogOpen, setMatchDayDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [formData, setFormData] = useState({
    home_team_id: "",
    away_team_id: "",
    match_date: "",
    match_time: "",
    match_type: "league",
  });
  const [matchDayData, setMatchDayData] = useState({
    date: "",
    games: [
      { home_team_id: "", away_team_id: "", time: "18:00" },
      { home_team_id: "", away_team_id: "", time: "19:00" },
      { home_team_id: "", away_team_id: "", time: "20:00" },
    ],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, matchesRes] = await Promise.all([
        getTeams(),
        getMatches(),
      ]);
      setTeams(teamsRes.data);
      setMatches(matchesRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const getTeam = (teamId) => teams.find((t) => t.id === teamId);

  const filteredMatches = matches.filter((m) => {
    if (activeTab === "all") return true;
    if (activeTab === "scheduled") return m.status === "scheduled";
    if (activeTab === "completed") return m.status === "completed";
    if (activeTab === "league") return m.match_type === "league";
    if (activeTab === "knockout")
      return ["semifinal1", "semifinal2", "final"].includes(m.match_type);
    return true;
  });

  // Helper to get match datetime
  const getMatchDateTime = (match) => {
    const dateStr = match.match_date || "1970-01-01";
    const timeStr = match.match_time || "00:00";
    return new Date(`${dateStr}T${timeStr}`);
  };

  // Separate upcoming and past matches
  const now = new Date();
  const upcomingMatches = filteredMatches
    .filter((match) => getMatchDateTime(match) >= now || match.status === "scheduled")
    .sort((a, b) => getMatchDateTime(a) - getMatchDateTime(b)); // Closest first

  const pastMatches = filteredMatches
    .filter((match) => getMatchDateTime(match) < now && match.status === "completed")
    .sort((a, b) => getMatchDateTime(b) - getMatchDateTime(a)); // Most recent first

  // Group upcoming matches by date
  const upcomingByDate = upcomingMatches.reduce((acc, match) => {
    const date = match.match_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(match);
    return acc;
  }, {});

  // Group past matches by date  
  const pastByDate = pastMatches.reduce((acc, match) => {
    const date = match.match_date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(match);
    return acc;
  }, {});

  const sortedUpcomingDates = Object.keys(upcomingByDate).sort();
  const sortedPastDates = Object.keys(pastByDate).sort((a, b) => b.localeCompare(a));

  const handleOpenDialog = (match = null) => {
    if (match) {
      setSelectedMatch(match);
      setFormData({
        home_team_id: match.home_team_id,
        away_team_id: match.away_team_id,
        match_date: match.match_date,
        match_time: match.match_time,
        match_type: match.match_type,
      });
    } else {
      setSelectedMatch(null);
      const today = new Date().toISOString().split("T")[0];
      setFormData({
        home_team_id: "",
        away_team_id: "",
        match_date: today,
        match_time: "18:00",
        match_type: "league",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMatch(null);
  };

  const handleOpenMatchDayDialog = () => {
    const today = new Date().toISOString().split("T")[0];
    setMatchDayData({
      date: today,
      games: [
        { home_team_id: "", away_team_id: "", time: "18:00" },
        { home_team_id: "", away_team_id: "", time: "19:00" },
        { home_team_id: "", away_team_id: "", time: "20:00" },
      ],
    });
    setMatchDayDialogOpen(true);
  };

  const handleCloseMatchDayDialog = () => {
    setMatchDayDialogOpen(false);
  };

  const addGameSlot = () => {
    const lastTime = matchDayData.games[matchDayData.games.length - 1]?.time || "20:00";
    const [hours] = lastTime.split(":");
    const nextHour = (parseInt(hours) + 1).toString().padStart(2, "0");
    setMatchDayData((prev) => ({
      ...prev,
      games: [...prev.games, { home_team_id: "", away_team_id: "", time: `${nextHour}:00` }],
    }));
  };

  const removeGameSlot = (index) => {
    if (matchDayData.games.length <= 1) return;
    setMatchDayData((prev) => ({
      ...prev,
      games: prev.games.filter((_, i) => i !== index),
    }));
  };

  const updateGameSlot = (index, field, value) => {
    setMatchDayData((prev) => ({
      ...prev,
      games: prev.games.map((game, i) =>
        i === index ? { ...game, [field]: value } : game
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.home_team_id ||
      !formData.away_team_id ||
      !formData.match_date ||
      !formData.match_time
    ) {
      toast.error("All fields are required");
      return;
    }

    if (formData.home_team_id === formData.away_team_id) {
      toast.error("Home and away teams must be different");
      return;
    }

    setSaving(true);
    try {
      if (selectedMatch) {
        await updateMatch(selectedMatch.id, {
          match_date: formData.match_date,
          match_time: formData.match_time,
        });
        toast.success("Match updated successfully");
      } else {
        await createMatch(formData);
        toast.success("Match scheduled successfully");
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save match");
    } finally {
      setSaving(false);
    }
  };

  const handleMatchDaySubmit = async (e) => {
    e.preventDefault();
    if (!matchDayData.date) {
      toast.error("Please select a date");
      return;
    }

    // Filter out empty games
    const validGames = matchDayData.games.filter(
      (g) => g.home_team_id && g.away_team_id && g.home_team_id !== g.away_team_id
    );

    if (validGames.length === 0) {
      toast.error("Please add at least one valid match");
      return;
    }

    setSaving(true);
    try {
      for (const game of validGames) {
        await createMatch({
          home_team_id: game.home_team_id,
          away_team_id: game.away_team_id,
          match_date: matchDayData.date,
          match_time: game.time,
          match_type: "league",
        });
      }
      toast.success(`${validGames.length} matches scheduled for ${matchDayData.date}`);
      handleCloseMatchDayDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule matches");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMatch) return;

    try {
      await deleteMatch(selectedMatch.id);
      toast.success("Match deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedMatch(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete match");
    }
  };

  const openDeleteDialog = (match) => {
    setSelectedMatch(match);
    setDeleteDialogOpen(true);
  };

  const formatMatchType = (type) => {
    const types = {
      league: "League",
      semifinal1: "Semi-Final 1",
      semifinal2: "Semi-Final 2",
      final: "Final",
    };
    return types[type] || type;
  };

  // Render a single match card
  const renderMatchCard = (match, index) => {
    const homeTeam = getTeam(match.home_team_id);
    const awayTeam = getTeam(match.away_team_id);

    return (
      <div
        key={match.id}
        className="bg-card border border-border rounded-xl p-4 card-hover animate-fade-in opacity-0"
        style={{ animationDelay: `${index * 30}ms` }}
        data-testid={`match-card-${match.id}`}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Time */}
          <div className="flex items-center gap-2 md:w-24">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="font-heading text-lg font-bold">
              {match.match_time}
            </span>
          </div>

          {/* Match Type Badge */}
          <div className="md:w-28">
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              {formatMatchType(match.match_type)}
            </span>
          </div>

          {/* Teams & Score */}
          <div className="flex-1 flex items-center justify-center gap-4">
            {/* Home Team */}
            <div className="flex items-center gap-2 flex-1 justify-end">
              <span className="font-medium text-right truncate">
                {homeTeam?.name || "TBD"}
              </span>
              {homeTeam?.logo ? (
                <img
                  src={homeTeam.logo}
                  alt={homeTeam.name}
                  className="w-10 h-10 rounded-lg object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-muted-foreground" />
                </div>
              )}
            </div>

            {/* Score */}
            <div className="px-4 text-center min-w-[80px]">
              {match.status === "completed" ? (
                <span className="font-heading text-2xl font-black score-display">
                  {match.home_score} - {match.away_score}
                </span>
              ) : (
                <span className="font-heading text-lg text-muted-foreground">
                  VS
                </span>
              )}
            </div>

            {/* Away Team */}
            <div className="flex items-center gap-2 flex-1">
              {awayTeam?.logo ? (
                <img
                  src={awayTeam.logo}
                  alt={awayTeam.name}
                  className="w-10 h-10 rounded-lg object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-muted-foreground" />
                </div>
              )}
              <span className="font-medium truncate">
                {awayTeam?.name || "TBD"}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 md:w-40 justify-end">
            <span
              className={`text-xs px-2 py-1 rounded-full ${
                match.status === "completed"
                  ? "bg-primary/20 text-primary"
                  : "bg-accent/20 text-accent"
              }`}
            >
              {match.status}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/dashboard/matches/${match.id}`)}
              data-testid={`enter-score-${match.id}`}
            >
              <Play className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleOpenDialog(match)}
              data-testid={`edit-match-${match.id}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openDeleteDialog(match)}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              data-testid={`delete-match-${match.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="matches-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight">
            Matches
          </h1>
          <p className="text-muted-foreground mt-2">
            Schedule and manage tournament matches
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleOpenMatchDayDialog}
            variant="secondary"
            data-testid="schedule-match-day-btn"
            disabled={teams.length < 2}
          >
            <CalendarPlus className="w-4 h-4 mr-2" />
            Match Day
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider btn-glow"
            data-testid="schedule-match-btn"
            disabled={teams.length < 2}
          >
            <Plus className="w-4 h-4 mr-2" />
            Single Match
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="all" data-testid="tab-all">
            All
          </TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">
            Scheduled
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">
            Completed
          </TabsTrigger>
          <TabsTrigger value="league" data-testid="tab-league">
            League
          </TabsTrigger>
          <TabsTrigger value="knockout" data-testid="tab-knockout">
            Knockout
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {(upcomingMatches.length > 0 || pastMatches.length > 0) ? (
            <div className="space-y-10">
              {/* UPCOMING MATCHES */}
              {upcomingMatches.length > 0 && (
                <div className="space-y-6">
                  <h2 className="text-lg font-bold uppercase text-primary flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Upcoming Matches ({upcomingMatches.length})
                  </h2>
                  {sortedUpcomingDates.map((date) => (
                    <div key={date}>
                      {/* Date Header */}
                      <div className="flex items-center gap-3 mb-4">
                        <Calendar className="w-5 h-5 text-muted-foreground" />
                        <h3 className="font-heading text-base font-bold uppercase tracking-wider">
                          {new Date(date).toLocaleDateString("en-US", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </h3>
                        <span className="text-sm text-muted-foreground">
                          ({upcomingByDate[date].length} {upcomingByDate[date].length === 1 ? "match" : "matches"})
                        </span>
                      </div>

                      {/* Day's Matches */}
                      <div className="space-y-3">
                        {upcomingByDate[date]
                          .sort((a, b) => (a.match_time || "").localeCompare(b.match_time || ""))
                          .map((match, index) => renderMatchCard(match, index))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* PAST MATCHES */}
              {pastMatches.length > 0 && (
                <div className="space-y-6">
                  <h2 className="text-lg font-bold uppercase text-muted-foreground flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Past Matches ({pastMatches.length})
                  </h2>
                  <div className="opacity-80">
                    {sortedPastDates.map((date) => (
                      <div key={date} className="mb-6">
                        {/* Date Header */}
                        <div className="flex items-center gap-3 mb-4">
                          <Calendar className="w-5 h-5 text-muted-foreground" />
                          <h3 className="font-heading text-base font-bold uppercase tracking-wider">
                            {new Date(date).toLocaleDateString("en-US", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </h3>
                          <span className="text-sm text-muted-foreground">
                            ({pastByDate[date].length} {pastByDate[date].length === 1 ? "match" : "matches"})
                          </span>
                        </div>

                        {/* Day's Matches */}
                        <div className="space-y-3">
                          {pastByDate[date]
                            .sort((a, b) => (b.match_time || "").localeCompare(a.match_time || ""))
                            .map((match, index) => renderMatchCard(match, index))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-12 text-center">
              <Calendar className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold uppercase mb-2">
                No Matches Found
              </h3>
              <p className="text-muted-foreground mb-6">
                {teams.length < 2
                  ? "Add at least 2 teams to schedule matches"
                  : "Schedule your first match day"}
              </p>
              {teams.length >= 2 && (
                <div className="flex gap-3 justify-center">
                  <Button
                    onClick={handleOpenMatchDayDialog}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider btn-glow"
                    data-testid="empty-schedule-match-day-btn"
                  >
                    <CalendarPlus className="w-4 h-4 mr-2" />
                    Schedule Match Day
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Single Match Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="bg-card border-border sm:max-w-md"
          data-testid="match-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-wider">
              {selectedMatch ? "Edit Match" : "Schedule Match"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            {!selectedMatch && (
              <>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Home Team
                  </Label>
                  <Select
                    value={formData.home_team_id}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, home_team_id: value }))
                    }
                  >
                    <SelectTrigger
                      className="bg-background border-border"
                      data-testid="home-team-select"
                    >
                      <SelectValue placeholder="Select home team" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Away Team
                  </Label>
                  <Select
                    value={formData.away_team_id}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, away_team_id: value }))
                    }
                  >
                    <SelectTrigger
                      className="bg-background border-border"
                      data-testid="away-team-select"
                    >
                      <SelectValue placeholder="Select away team" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {teams
                        .filter((t) => t.id !== formData.home_team_id)
                        .map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Match Type
                  </Label>
                  <Select
                    value={formData.match_type}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, match_type: value }))
                    }
                  >
                    <SelectTrigger
                      className="bg-background border-border"
                      data-testid="match-type-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="league">League</SelectItem>
                      <SelectItem value="semifinal1">Semi-Final 1</SelectItem>
                      <SelectItem value="semifinal2">Semi-Final 2</SelectItem>
                      <SelectItem value="final">Final</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Date
                </Label>
                <Input
                  type="date"
                  value={formData.match_date}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      match_date: e.target.value,
                    }))
                  }
                  className="bg-background border-border"
                  data-testid="match-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Time
                </Label>
                <Input
                  type="time"
                  value={formData.match_time}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      match_time: e.target.value,
                    }))
                  }
                  className="bg-background border-border"
                  data-testid="match-time-input"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCloseDialog}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider"
                data-testid="save-match-btn"
              >
                {saving ? "Saving..." : selectedMatch ? "Update" : "Schedule"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Match Day Dialog */}
      <Dialog open={matchDayDialogOpen} onOpenChange={setMatchDayDialogOpen}>
        <DialogContent
          className="bg-card border-border sm:max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="match-day-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-wider">
              Schedule Match Day
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMatchDaySubmit} className="space-y-6">
            {/* Date Selection */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Match Day Date
              </Label>
              <Input
                type="date"
                value={matchDayData.date}
                onChange={(e) =>
                  setMatchDayData((prev) => ({ ...prev, date: e.target.value }))
                }
                className="bg-background border-border w-full md:w-48"
                data-testid="match-day-date-input"
              />
            </div>

            {/* Games List */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Games ({matchDayData.games.length})
                </Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={addGameSlot}
                  className="text-primary"
                  data-testid="add-game-slot-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Game
                </Button>
              </div>

              {matchDayData.games.map((game, index) => (
                <div
                  key={index}
                  className="bg-background rounded-xl p-4 space-y-4"
                  data-testid={`game-slot-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-muted-foreground">
                      Game {index + 1}
                    </span>
                    {matchDayData.games.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeGameSlot(index)}
                        className="text-destructive hover:text-destructive h-6 w-6 p-0"
                        data-testid={`remove-game-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Time */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Time</Label>
                      <Input
                        type="time"
                        value={game.time}
                        onChange={(e) => updateGameSlot(index, "time", e.target.value)}
                        className="bg-card border-border"
                        data-testid={`game-time-${index}`}
                      />
                    </div>

                    {/* Home Team */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Home Team</Label>
                      <Select
                        value={game.home_team_id}
                        onValueChange={(value) => updateGameSlot(index, "home_team_id", value)}
                      >
                        <SelectTrigger
                          className="bg-card border-border"
                          data-testid={`game-home-${index}`}
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {teams.map((team) => (
                            <SelectItem key={team.id} value={team.id}>
                              {team.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Away Team */}
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Away Team</Label>
                      <Select
                        value={game.away_team_id}
                        onValueChange={(value) => updateGameSlot(index, "away_team_id", value)}
                      >
                        <SelectTrigger
                          className="bg-card border-border"
                          data-testid={`game-away-${index}`}
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {teams
                            .filter((t) => t.id !== game.home_team_id)
                            .map((team) => (
                              <SelectItem key={team.id} value={team.id}>
                                {team.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCloseMatchDayDialog}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider"
                data-testid="save-match-day-btn"
              >
                {saving ? "Scheduling..." : "Schedule Matches"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-bold uppercase">
              Delete Match
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this match? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-match-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
